import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { cn } from "@faire/web--source/tools/cn";
// eslint-disable-next-line no-restricted-imports
import { assignInlineVars } from "@vanilla-extract/dynamic";

export interface ISDUIResponsiveValue<T> {
  mobile_and_above: T | undefined;
  tablet_and_above: T | undefined;
  desktop_and_above: T | undefined;
  x_large_and_above?: T | undefined;
  xx_large_and_above?: T | undefined;
}

export const getResponsiveValueAtIndex = <T>(
  responsiveValues: ResponsiveValues<T[]>,
  index: number,
  defaultValue: T
): ResponsiveValues<T> => {
  return {
    mobileAndAbove: responsiveValues.mobileAndAbove[index] ?? defaultValue,
    tabletAndAbove: responsiveValues.tabletAndAbove?.[index],
    desktopAndAbove: responsiveValues.desktopAndAbove?.[index],
  };
};

export const convertServerResponsivePropertyToClientResponsiveProperty = <
  T,
  U extends ISDUIResponsiveValue<T>,
  V,
>(
  defaultValue: T,
  convertServerToClientValue: (value: T) => V,
  serverResponsiveValues?: U
): ResponsiveValues<V> => {
  const values: ResponsiveValues<V> = {
    mobileAndAbove: convertServerToClientValue(
      serverResponsiveValues?.mobile_and_above ?? defaultValue
    ),
  };
  const tabletAndAbove =
    serverResponsiveValues?.tablet_and_above !== undefined
      ? convertServerToClientValue(serverResponsiveValues?.tablet_and_above)
      : undefined;
  const desktopAndAbove =
    serverResponsiveValues?.desktop_and_above !== undefined
      ? convertServerToClientValue(serverResponsiveValues?.desktop_and_above)
      : undefined;
  const xLargeAndAbove =
    serverResponsiveValues?.x_large_and_above !== undefined
      ? convertServerToClientValue(serverResponsiveValues?.x_large_and_above)
      : undefined;
  const xxLargeDesktop =
    serverResponsiveValues?.xx_large_and_above !== undefined
    ? convertServerToClientValue(serverResponsiveValues?.xx_large_and_above)
      : undefined;

  if (tabletAndAbove !== undefined) {
    Object.assign(values, { tabletAndAbove });
  }
  if (desktopAndAbove !== undefined) {
    Object.assign(values, { desktopAndAbove });
  }
  if (xLargeAndAbove !== undefined) {
    Object.assign(values, { xLargeAndAbove });
  }
  if (xxLargeDesktop !== undefined) {
    Object.assign(values, { xxLargeDesktop });
  }
  return values;
};

export const getResponsiveStylingClassNames = <T>(
  responsiveValues: ResponsiveValues<T>,
  responsiveClassNamesMap: Partial<Record<keyof ResponsiveValues<T>, string>>
) => {
  return Object.entries(responsiveValues)
    .filter(([_, value]) => value !== undefined)
    .map(([key]) => responsiveClassNamesMap[key as keyof ResponsiveValues<T>])
    .filter(isNotUndefined);
};

/**
 * Given a set of responsive values for certain viewports and a mapping of
 * those viewports to a desired style classname and variable name,
 * return all valid classnames and their associated style variables.
 *
 * E.g. if mobileAndAbove's value should be in var1 and should be applied to class1,
 * the input should be `{mobileAndAbove: value1}, {"mobileAndAbove": ["class1", "var1"]}`
 *
 * Optionally, provide a function to transform it into a desired CSS value.
 *
 * E.g. if value1 is 12 but we want 12px, getStyleForVarName should be `(value: number) => value + "px"`
 */
export const getResponsiveStyles = <T>(
  responsiveValues: ResponsiveValues<T>,
  classNamesAndVars: Partial<
    Record<keyof ResponsiveValues<T>, [className: string, varName: string]>
  >,
  getStyleForVarName: (value: T) => string = (value) => `${value}`
) => {
  const computedClassNames = Object.entries(responsiveValues)
    .map(([key, value]) =>
      value
        ? classNamesAndVars[key as keyof ResponsiveValues<T>]?.[0]
        : undefined
    )
    .filter(isNotUndefined);

  const computedStyleVars = Object.entries(responsiveValues).reduce<
    Partial<Record<keyof ResponsiveValues<T>, string>>
  >((acc, [key, value]) => {
    const [, varName] =
      classNamesAndVars[key as keyof ResponsiveValues<T>] ?? [];
    if (!value || !varName) {
      return acc;
    }
    return {
      ...acc,
      [varName]: getStyleForVarName(value),
    };
  }, {});

  return {
    className: cn(computedClassNames),
    style: assignInlineVars(computedStyleVars),
  };
};
